import { ZipJobConstants } from '../types/constants'
import { ExternalLink } from './../types/page'
import { isBrowser } from './env'

const WWW_PREFIX = 'www.'

/* eslint-disable @typescript-eslint/no-var-requires */
const URLSearchParams = (() =>
  // @ts-ignore
  typeof window !== 'undefined'
    ? // @ts-ignore
      window.URLSearchParams
    : require('url').URLSearchParams)()

export interface UrlPathJoinOptions {
  trailingSlash?: boolean
  search?: Record<string, string> | URLSearchParams
  hash?: string
  host?: string
  protocol?: string
  baseURL?: string
}

const endsWithSlash = /\/$/

/**
 * Given an array of path strings, join them together in a way that is safe for
 * relative URL linking.
 */
export const urlPathJoin = (
  paths: string[],
  options: UrlPathJoinOptions = {}
): string => {
  const cleanedPaths = paths
    .filter(Boolean)
    .flatMap((p) => p.split('/').filter(Boolean).map(encodeURIComponent))

  // If the path ends with the word index, treat it like index.html and remove
  // it.
  if (cleanedPaths[cleanedPaths.length - 1] === 'index') {
    cleanedPaths.pop()
  }

  let joinedPaths = '/' + cleanedPaths.join('/')

  if (options.trailingSlash && !endsWithSlash.test(joinedPaths)) {
    joinedPaths += '/'
  }

  if (options.search) {
    const search = options.search
    let modifiedSearch = search

    if (!(search instanceof URLSearchParams)) {
      modifiedSearch = Object.entries(search).reduce((search, [key, value]) => {
        search.append(key, value)
        return search
      }, new URLSearchParams())
    }

    joinedPaths += `?${modifiedSearch.toString()}`
  }

  if (options.hash) {
    joinedPaths += `#${options.hash}`
  }

  if (options.host) {
    const { host, protocol = 'https' } = options
    joinedPaths = `${protocol}://${host}${joinedPaths}`
  } else if (options.baseURL) {
    joinedPaths = options.baseURL + joinedPaths
  }

  joinedPaths = joinedPaths.replace(WWW_PREFIX, '')

  return joinedPaths
}

export const getExternalLinkURL = (link: ExternalLink): string => {
  switch (process.env.NODE_ENV) {
    case 'staging':
      return link.stagingUrl ?? link.url

    case 'development':
      return link.devUrl ?? link.url

    default:
      return link.url
  }
}

export const appendZjSpecificTokens = (url: string): string => {
  if (!isBrowser) {
    return url
  }

  const browserDt = new URLSearchParams(window.location.search).get('dt')
  const zjImpactPartnerToken = sessionStorage.getItem(
    ZipJobConstants.ImpactPartnerKey
  )

  const [urlWithoutParams, search] = url.split('?')
  const params = new URLSearchParams(search)

  if (!params.has('dt')) {
    if (browserDt) {
      params.set('dt', browserDt)
    }
  }

  if (zjImpactPartnerToken) {
    params.set(ZipJobConstants.ImpactPartnerKey, zjImpactPartnerToken)
  }

  if (params.size) {
    return urlPathJoin([urlWithoutParams], {
      search: {
        ...Object.fromEntries(params),
      },
    })
  }

  return url
}

export const getRawUrlAndSearchFromUrl = (
  url: string
): [string, URLSearchParams] => {
  const [rawUrl, search = ''] = url.split('?')

  return [rawUrl, new URLSearchParams(search)]
}
